<template>
  <div ref="previewRoot" class="native-preview">
    <iframe
      v-if="shouldPlay"
      ref="previewFrame"
      class="preview-frame"
      :class="{ 'is-landscape': previewLandscape }"
      :src="previewUrl"
      :style="previewFrameStyle"
      title="Cloud phone preview"
      tabindex="-1"
    ></iframe>
  </div>
</template>

<script setup>
/* global defineEmits, defineExpose, defineProps */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { closeNativeSession } from '../../api/nativeMedia'

const props = defineProps({
  deviceId: {
    type: [Number, String],
    required: true
  }
})
const emit = defineEmits(['preview-error', 'preview-status'])

const previewUrl = computed(() => `${process.env.BASE_URL}preview/${props.deviceId}`)
const previewRoot = ref(null)
const previewFrame = ref(null)
const shouldPlay = ref(false)
const previewLandscape = ref(false)
const previewSize = ref({ width: 0, height: 0 })

const previewFrameStyle = computed(() => {
  if (!previewLandscape.value
    || previewSize.value.width <= 0
    || previewSize.value.height <= 0) {
    return null
  }

  return {
    width: `${previewSize.value.height}px`,
    height: `${previewSize.value.width}px`
  }
})

let visibilityObserver = null
let sizeObserver = null
let closeTimer = null
let pendingCloseRequest = null
let previewRouteId = null

function cancelDelayedClose() {
  if (closeTimer !== null) {
    window.clearTimeout(closeTimer)
    closeTimer = null
  }
}

function startPreview() {
  cancelDelayedClose()

  if (!shouldPlay.value) {
    previewLandscape.value = false
    previewRouteId = null
  }

  shouldPlay.value = true
}

function schedulePreviewClose() {
  cancelDelayedClose()

  closeTimer = window.setTimeout(() => {
    shouldPlay.value = false
    previewRouteId = null
    closeTimer = null
  }, 3000)
}

function updatePreviewSize() {
  if (!previewRoot.value) {
    return
  }

  previewSize.value = {
    width: previewRoot.value.clientWidth,
    height: previewRoot.value.clientHeight
  }
}

function handlePreviewMessage(event) {
  if (event.origin !== window.location.origin
    || event.source !== previewFrame.value?.contentWindow) {
    return
  }

  const message = event.data

  if (message?.type === 'cloudphone-native-preview-status'
    && message.deviceId === String(props.deviceId)) {
    emit('preview-status', { deviceId: props.deviceId, status: message.status })
    return
  }

  if (message?.type === 'cloudphone-native-preview-route-opened'
    && message.deviceId === String(props.deviceId)) {
    previewRouteId = message.routeId || null
    return
  }

  if (message?.type === 'cloudphone-native-preview-error'
    && message.deviceId === String(props.deviceId)) {
    emit('preview-error', {
      deviceId: props.deviceId,
      message: message.message
    })
    return
  }

  if (message?.type === 'cloudphone-native-preview-closed'
    && message.deviceId === String(props.deviceId)
    && pendingCloseRequest?.requestId === message.requestId) {
    window.clearTimeout(pendingCloseRequest.timeoutId)
    pendingCloseRequest.resolve()
    pendingCloseRequest = null
    shouldPlay.value = false
    return
  }

  if (message?.type !== 'cloudphone-native-preview-orientation'
    || message.deviceId !== String(props.deviceId)) {
    return
  }

  previewLandscape.value = Boolean(message.landscape)
}

async function closePreviewForDetail() {
  cancelDelayedClose()

  const routeId = previewRouteId
  const frameWindow = previewFrame.value?.contentWindow

  if (routeId) {
    previewRouteId = null
    shouldPlay.value = false
    await nextTick()

    try {
      await closeNativeSession(routeId)
    } catch (error) {
      if (error.response?.status !== 404) {
        throw error
      }
    }

    return
  }

  if (!shouldPlay.value || !frameWindow) {
    shouldPlay.value = false
    return
  }

  if (pendingCloseRequest) {
    return pendingCloseRequest.promise
  }

  const requestId = Math.random().toString(36).slice(2)
  let resolveClose
  const promise = new Promise((resolve) => {
    resolveClose = resolve
  })
  const timeoutId = window.setTimeout(() => {
    if (pendingCloseRequest?.requestId !== requestId) {
      return
    }

    pendingCloseRequest.resolve()
    pendingCloseRequest = null
    shouldPlay.value = false
  }, 4000)

  pendingCloseRequest = {
    requestId,
    promise,
    resolve: resolveClose,
    timeoutId
  }

  frameWindow.postMessage({
    type: 'cloudphone-native-close-preview',
    deviceId: String(props.deviceId),
    requestId
  }, window.location.origin)

  return promise
}

onMounted(() => {
  updatePreviewSize()
  window.addEventListener('message', handlePreviewMessage)

  if (typeof ResizeObserver !== 'undefined') {
    sizeObserver = new ResizeObserver(updatePreviewSize)
    sizeObserver.observe(previewRoot.value)
  }

  if (typeof IntersectionObserver === 'undefined') {
    shouldPlay.value = true
    return
  }

  visibilityObserver = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      startPreview()
      return
    }

    schedulePreviewClose()
  }, {
    threshold: 0.15
  })

  visibilityObserver.observe(previewRoot.value)
})

onBeforeUnmount(() => {
  cancelDelayedClose()
  const routeId = previewRouteId
  previewRouteId = null
  if (routeId) {
    void closeNativeSession(routeId).catch(error => console.warn('关闭已移除的预览会话失败', error))
  }
  if (pendingCloseRequest) {
    window.clearTimeout(pendingCloseRequest.timeoutId)
    pendingCloseRequest.resolve()
    pendingCloseRequest = null
  }
  visibilityObserver?.disconnect()
  sizeObserver?.disconnect()
  window.removeEventListener('message', handlePreviewMessage)
})

defineExpose({
  closePreviewForDetail
})
</script>

<style scoped>
.native-preview {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: #090e13;
}

.preview-frame {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  pointer-events: none;
}

.preview-frame.is-landscape {
  top: 50%;
  right: auto;
  bottom: auto;
  left: 50%;
  transform: translate(-50%, -50%) rotate(90deg);
  transform-origin: center center;
}
</style>
